import {
  automationDryRunResponseSchema,
  automationLookupsSchema,
  automationRuleListResponseSchema,
  automationRuleRunListResponseSchema,
  automationRuleSchema,
  okResponseSchema,
  type AutomationDryRunRequest,
  type UpsertAutomationRuleRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const automationLookupsQueryKey = ['automation', 'lookups'] as const;
export const automationRulesQueryKey = ['automation', 'rules'] as const;
export const automationRunsQueryKey = (ruleId: string) => ['automation', 'runs', ruleId] as const;

export function useAutomationLookups() {
  return useQuery({
    queryKey: automationLookupsQueryKey,
    queryFn: () => apiFetch('/automation/lookups', automationLookupsSchema),
  });
}

export function useAutomationRules() {
  return useQuery({
    queryKey: automationRulesQueryKey,
    queryFn: () => apiFetch('/automation/rules', automationRuleListResponseSchema),
  });
}

export function useAutomationRuns(ruleId: string | null) {
  return useQuery({
    queryKey: automationRunsQueryKey(ruleId ?? ''),
    queryFn: () =>
      apiFetch(`/automation/rules/${ruleId}/runs`, automationRuleRunListResponseSchema),
    enabled: ruleId !== null,
  });
}

export function useCreateAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertAutomationRuleRequest) =>
      apiFetch('/automation/rules', automationRuleSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationRulesQueryKey });
    },
  });
}

export function useUpdateAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertAutomationRuleRequest }) =>
      apiFetch(`/automation/rules/${id}`, automationRuleSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationRulesQueryKey });
    },
  });
}

export function useSetAutomationRuleActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiFetch(`/automation/rules/${id}/active`, automationRuleSchema, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationRulesQueryKey });
    },
  });
}

export function useDeleteAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/automation/rules/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: automationRulesQueryKey });
    },
  });
}

export function useAutomationDryRun() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AutomationDryRunRequest }) =>
      apiFetch(`/automation/rules/${id}/dry-run`, automationDryRunResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
